import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import AdminTableHeader from '../../../components/Admin/AdminTableHeader';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import PaginationButtons from '../../../components/Common/Pagination';
import { LogModel } from '../../../interfaces/log.interface';
import { ErrorLogsFilters, fetchErrorLogs, searchErrorLogs } from './services/ErrorLogs.service';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import { convertTimestampToDateTime } from '../../../helper/helper';
import BookPage from '../../BookPage/BookPage';
import { AdminNavHandler } from '../adminNav';

type Option = { value: string; label: string };

const ERROR_CODE_OPTIONS: Option[] = [
  { value: '', label: 'All Codes' },
  { value: 'CONTENT_FILTERED', label: 'Content Filtered' },
  { value: 'PROVIDER_ERROR', label: 'Provider Error' },
  { value: 'PROVIDER_TIMEOUT', label: 'Provider Timeout' },
  { value: 'PROVIDER_CONNECTION_ERROR', label: 'Provider Connection Error' },
  { value: 'PROVIDER_RATE_LIMITED', label: 'Provider Rate Limited' },
  { value: 'PROVIDER_INSUFFICIENT_CREDITS', label: 'Provider Insufficient Credits' },
  { value: 'UPSTREAM_TIMEOUT', label: 'Upstream Timeout' },
  { value: 'TIMEOUT', label: 'Timeout' },
  { value: 'JOB_ABANDONED', label: 'Job Abandoned' },
  { value: 'INVALID_PARAMS', label: 'Invalid Params' },
  { value: 'WEBHOOK_DELIVERY_FAILED', label: 'Webhook Delivery Failed' },
  { value: 'GCS_UPLOAD_FAILED', label: 'GCS Upload Failed' },
  { value: 'PRESIGNED_URL_FAILED', label: 'Presigned URL Failed' },
  { value: 'PROMPT_GENERATION_FAILED', label: 'Prompt Generation Failed' },
  { value: 'QUEUE_FULL', label: 'Queue Full' },
  { value: 'WEBP_CONVERSION_FAILED', label: 'WebP Conversion Failed' },
  { value: 'FIRESTORE_UPDATE_FAILED', label: 'Firestore Update Failed' },
  { value: 'IMAGE_DOWNLOAD_FAILED', label: 'Image Download Failed' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

const PROVIDER_OPTIONS: Option[] = [
  { value: '', label: 'All Providers' },
  { value: 'runware', label: 'Runware' },
  { value: 'leonardo', label: 'Leonardo' },
  { value: 'together', label: 'Together' },
  { value: 'imagineapi', label: 'ImagineAPI' },
  { value: 'openai', label: 'OpenAI' },
];

const SOURCE_OPTIONS: Option[] = [
  { value: '', label: 'All Sources' },
  { value: 'image-gen-api', label: 'image-gen-api' },
  { value: 'storybuilder', label: 'storybuilder' },
  { value: 'runware-webhook', label: 'runware-webhook' },
  { value: 'webhook', label: 'webhook' },
  { value: 'polling-service', label: 'polling-service' },
];

const MODULE_OPTIONS: Option[] = [
  { value: '', label: 'All Modules' },
  { value: 'character_generation', label: 'Character Generation' },
  { value: 'image_generation', label: 'Image Generation' },
  { value: 'email_sending', label: 'Email Sending' },
];

const SEVERITY_OPTIONS: Option[] = [
  { value: '', label: 'Errors & Warnings' },
  { value: 'error', label: 'Errors only' },
  { value: 'warning', label: 'Warnings only' },
];

const MakeFormatString = (str: string) =>
  str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const TABLE_COLUMNS = [
  { key: 'timestamp', label: 'Timestamp' },
  { key: 'severity', label: 'Severity' },
  { key: 'module', label: 'Module' },
  { key: 'errorCode', label: 'Error Code' },
  { key: 'errorProvider', label: 'Provider' },
  { key: 'errorSource', label: 'Source' },
  { key: 'failedPrompt', label: 'Original / Failed Prompt', minWidth: '20rem' },
  { key: 'softenedPrompt', label: 'Softened Prompt', minWidth: '16rem' },
  { key: 'error', label: 'Error / Stack', minWidth: '16rem' },
  { key: 'job_id', label: 'Job Id' },
  { key: 'status', label: 'Status' },
  { key: 'user', label: 'User' },
  { key: 'book', label: 'Book' },
];

const PAGE_SIZE = 25;

interface ErrorLogsProps {
  onOptionClick?: AdminNavHandler;
}

const ErrorLogs = ({ onOptionClick }: ErrorLogsProps) => {
  const [loading, setLoading] = useState(true);
  const [errorLogs, setErrorLogs] = useState<LogModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [errorCodeFilter, setErrorCodeFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [stackFilter, setStackFilter] = useState('');
  const [bookPageId, setBookPageId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  }); // intentionally no deps — matches Schools/Districts pattern

  const filters: ErrorLogsFilters = useMemo(() => ({
    errorCode: errorCodeFilter || null,
    errorProvider: providerFilter || null,
    errorSource: sourceFilter || null,
    module: moduleFilter || null,
    severity: severityFilter || null,
  }), [errorCodeFilter, providerFilter, sourceFilter, moduleFilter, severityFilter]);

  const loadLogs = async (page: number, f: ErrorLogsFilters) => {
    setLoading(true);
    try {
      const { errorLogs: logs, totalPages: pages } = await fetchErrorLogs(page, f);
      setErrorLogs(logs);
      setTotalPages(pages || 0);
      setTotalRecords(logs.length + (page - 1) * PAGE_SIZE); // best estimate without total count from API
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(currentPage, filters);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadLogs(1, filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [errorCodeFilter, providerFilter, sourceFilter, moduleFilter, severityFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value: string) => {
    searchErrorLogs(value).then((logs) => setErrorLogs(logs)).catch(() => {});
  };

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages) setCurrentPage(+page);
  };

  const displayedLogs = useMemo(() => {
    if (!stackFilter) return errorLogs;
    const needle = stackFilter.toLowerCase();
    return errorLogs.filter(
      (log) =>
        (log.errorStack || '').toLowerCase().includes(needle) ||
        (log.error || '').toLowerCase().includes(needle),
    );
  }, [errorLogs, stackFilter]);

  if (bookPageId) {
    return (
      <div className="p-0 md:p-4">
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-storyblue hover:underline mb-4"
          onClick={(e) => { e.stopPropagation(); setBookPageId(null); }}
        >
          ← Back to error logs
        </button>
        <BookPage bookIdAdmin={bookPageId} />
      </div>
    );
  }

  return (
    <div className="p-0 md:p-4">
      {/* Toolbar */}
      <div className="flex justify-between items-center flex-wrap gap-2 my-5">
        <div className="flex items-center gap-2 flex-wrap">
          <AdminSearchInput
            placeholder="Search user, book, stack..."
            onSearch={handleSearchChange}
            className="w-64"
          />
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
            title="Filter by severity"
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {MODULE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={errorCodeFilter}
            onChange={(e) => setErrorCodeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {ERROR_CODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Filter stack/message"
            value={stackFilter}
            onChange={(e) => setStackFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-44"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadLogs(currentPage, filters)}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:shadow-md transition-shadow text-sm border border-gray-300 disabled:opacity-50"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="w-full overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <AdminTableHeader columns={TABLE_COLUMNS} />
              <tbody>
                {displayedLogs.length > 0 ? (
                  displayedLogs.map((log, index) => {
                    const severity = log.severity || 'error';
                    const isWarning = severity === 'warning';
                    return (
                    <tr key={log.id || index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                      <td className="text-left py-2 px-4 border-b text-xs whitespace-nowrap text-gray-500">
                        {convertTimestampToDateTime(log.timestamp)}
                      </td>
                      <td className="text-left py-2 px-4 border-b">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          isWarning ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'
                        }`}>
                          {isWarning ? 'Warning' : 'Error'}
                        </span>
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs">
                        {MakeFormatString(log.module)}
                      </td>
                      <td className="text-left py-2 px-4 border-b">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          log.errorCode
                            ? (isWarning ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700')
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {log.errorCode ? MakeFormatString(log.errorCode) : '—'}
                        </span>
                      </td>
                      <td className="text-left py-2 px-4 border-b">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          log.errorProvider ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {log.errorProvider || '—'}
                        </span>
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs text-gray-500">
                        {log.errorSource || '—'}
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs" style={{ minWidth: '20rem', maxWidth: '24rem' }}>
                        {log.failedPrompt ? (
                          <div className="break-words whitespace-pre-wrap text-gray-700 font-mono text-[0.7rem]">{log.failedPrompt}</div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs" style={{ minWidth: '16rem', maxWidth: '20rem' }}>
                        {log.softenedPrompt ? (
                          <div className="break-words whitespace-pre-wrap text-amber-900 font-mono text-[0.7rem]">{log.softenedPrompt}</div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="text-left py-2 px-4 border-b" style={{ minWidth: '16rem', maxWidth: '20rem' }}>
                        {log.error && (
                          <div className="text-xs font-medium text-gray-900 mb-1 break-words">{log.error}</div>
                        )}
                        {log.errorStack && (() => {
                          try {
                            const obj = JSON.parse(log.errorStack);
                            return (
                              <details className="text-xs">
                                <summary className="cursor-pointer text-gray-400 hover:text-gray-700">stack</summary>
                                <pre className="whitespace-pre-wrap break-words font-mono mt-1">{JSON.stringify(obj, null, 2)}</pre>
                              </details>
                            );
                          } catch {
                            return (
                              <details className="text-xs">
                                <summary className="cursor-pointer text-gray-400 hover:text-gray-700">stack</summary>
                                <pre className="whitespace-pre-wrap break-words mt-1">{log.errorStack}</pre>
                              </details>
                            );
                          }
                        })()}
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs font-mono text-gray-600">
                        {log.job_id || '—'}
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs capitalize">{log.status}</td>
                      <td className="text-left py-2 px-4 border-b text-xs">
                        {log.user && onOptionClick ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOptionClick('Customers', { customerEmail: log.user }); }}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                            title="View customer details"
                          >
                            {log.user}
                          </button>
                        ) : (
                          log.user || '—'
                        )}
                      </td>
                      <td className="text-left py-2 px-4 border-b text-xs">
                        {log.bookTitle ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setBookPageId(String(log.bookId)); }}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                            title="View book (page view)"
                          >
                            {log.bookTitle}
                          </button>
                        ) : '—'}
                        <div className="font-mono text-gray-400">{log.bookId || ''}</div>
                        {log.pageKey && <div className="text-gray-400">pg: {log.pageKey}</div>}
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <NoRecordFound colSpan={TABLE_COLUMNS.length} />
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-4">
            <TableInfoBar
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              totalPages={totalPages}
              recordsOnPage={displayedLogs.length}
              label="logs"
            />
            {totalPages > 1 && !stackFilter && (
              <PaginationButtons
                totalPages={totalPages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ErrorLogs;
